/**
 * mock-paper-composer.ts
 *
 * Assembles a mock exam paper from *ready* KPs for a given subject.
 *
 * Midterm (30 Q): front-half chapters 60% + back-half chapters 40%
 * Final   (50 Q): all chapters even weight
 *
 * Difficulty mix: 30% d1 + 50% d2 + 20% d3
 *
 * Questions are sourced from:
 *   1. JSON banks at /data/<bankFile>.json  (BankJson format)
 *   2. Math generator functions (mulberry32-seeded, deterministic)
 *
 * Determinism: if rngSeed is provided, the same paper is returned every call.
 */

import type { GeneratedQuestion, Difficulty } from '../generators/math/types';
import type { Subject } from '../../shared/types';
import { CURRICULUM } from '../curriculum/index';

// ─── Mulberry32 PRNG ────────────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle<T>(arr: T[], rand: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MockPaperOpts {
  subject: Subject;
  level: 'midterm' | 'final';
  rngSeed?: number;
}

/** A question as it appears in a bank JSON file */
export type BankQuestion = GeneratedQuestion & {
  surfaceVariant: string;
  /** chapter number (1-based) within the subject curriculum */
  chapterNumber?: number;
};

// ─── Difficulty allocation ───────────────────────────────────────────────────

const DIFFICULTY_WEIGHTS: Difficulty[] = [
  // 30% d1, 50% d2, 20% d3  — represented as a pool of 10
  1, 1, 1, 2, 2, 2, 2, 2, 3, 3,
];

function pickDifficulty(rand: () => number): Difficulty {
  return DIFFICULTY_WEIGHTS[Math.floor(rand() * DIFFICULTY_WEIGHTS.length)];
}

// ─── Bank-file registry ──────────────────────────────────────────────────────
//
// Maps kpId → public URL path for pre-built JSON banks.
// Generator-backed KPs are handled separately in MATH_GENERATORS.

const BANK_FILES: Record<string, string> = {
  // Math JSON banks
  'math.calc.addsub-vertical': '/data/math-weight-康軒.json', // reuse closest bank
  'math.weight.kg':            '/data/math-weight-康軒.json',
  'math.angle.basic':          '/data/math-angle-康軒.json',
  'math.stats.table':          '/data/math-table-康軒.json',
  'math.length.cm':            '/data/math-length-康軒.json',
  'math.decimal.addsub':       '/data/math-weight-康軒.json', // fallback until dedicated bank

  // Chinese JSON banks
  'chinese.zhuyin.basic':      '/data/chinese-zhuyin-康軒.json',
  'chinese.phonetic.tone':     '/data/chinese-phonetic-康軒.json',
  'chinese.vocab.usage':       '/data/chinese-vocab-usage-康軒.json',
  'chinese.segment.summary':   '/data/chinese-segment-康軒.json',
  'chinese.sentence.pattern':  '/data/chinese-sentence-康軒.json',
  'chinese.comprehension.basic': '/data/chinese-read-康軒.json',
  'chinese.writing.guided':    '/data/chinese-write-康軒.json',

  // English JSON banks
  'english.alphabet.phonics':  '/data/english-alphabet-翰林.json',
  'english.phonics.basic':     '/data/english-phonics-翰林.json',
  'english.vocab.family':      '/data/english-vocab-family-翰林.json',
  'english.vocab.school':      '/data/english-vocab-school-翰林.json',
  'english.vocab.colors':      '/data/english-colors-翰林.json',
  'english.dialogue.daily':    '/data/english-greetings-翰林.json',

  // Nature JSON banks
  'nature.plant.lifecycle':    '/data/nature-plant-lifecycle-南一.json',
  'nature.animal.classify':    '/data/nature-animals-南一.json',
  'nature.water.states':       '/data/nature-water-states-南一.json',
  'nature.weather.seasons':    '/data/nature-weather-南一.json',
  'nature.machine.basic':      '/data/nature-force-南一.json',
  'nature.sound.light.basic':        '/data/nature-sound-light-南一.json',
  'nature.rock.basic':         '/data/nature-rock-南一.json',

  // Social JSON banks
  'social.community.basic':    '/data/social-community-翰林.json',
  'social.taiwan.geography':   '/data/social-taiwan-geography-翰林.json',
  'social.hometown.basic':     '/data/social-hometown-翰林.json',
  'social.custom.festival':    '/data/social-festival-翰林.json',
  'social.life.rule':          '/data/social-life-rule-翰林.json',
  'social.traffic.basic':      '/data/social-transport-翰林.json',
  'social.environment.basic':  '/data/social-environment-翰林.json',
};

// ─── Math generator type ──────────────────────────────────────────────────────

type MathGenFn = (seed: number, d: Difficulty) => GeneratedQuestion;

// ─── Async bank loader ───────────────────────────────────────────────────────

const bankCache = new Map<string, BankQuestion[]>();

async function loadBankQuestions(url: string, chapterNumber: number): Promise<BankQuestion[]> {
  const cacheKey = `${url}#${chapterNumber}`;
  if (bankCache.has(cacheKey)) return bankCache.get(cacheKey)!;

  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Bank load failed: ${url} (${resp.status})`);
  const json = await resp.json() as { questions: BankQuestion[] };
  const tagged = json.questions.map(q => ({ ...q, chapterNumber }));
  bankCache.set(cacheKey, tagged);
  return tagged;
}

// ─── Core logic ──────────────────────────────────────────────────────────────

/**
 * Load generator for a math KP dynamically (avoids top-level static imports).
 * Returns null when no generator exists for this KP.
 */
async function loadMathGen(kpId: string): Promise<MathGenFn | null> {
  const genMap: Record<string, () => Promise<{ [k: string]: MathGenFn }>> = {
    'math.mul.2x1':              () => import('../generators/math/mul2x1').then(m => ({ generate: m.generateMul2x1 })),
    'math.mul.2x2':              () => import('../generators/math/mul2x2').then(m => ({ generate: m.generateMul2x2 })),
    'math.mul.3x1':              () => import('../generators/math/mul3x1').then(m => ({ generate: m.generateMul3x1 })),
    'math.div.remainder':        () => import('../generators/math/div-remainder').then(m => ({ generate: m.generateDivRemainder })),
    'math.div.no-remainder':     () => import('../generators/math/div2x1').then(m => ({ generate: m.generateDiv2x1 })),
    'math.div.mul-relation':     () => import('../generators/math/div3x1').then(m => ({ generate: m.generateDiv3x1 })),
    'math.word.two-step':        () => import('../generators/math/two-step').then(m => ({ generate: m.generateTwoStep })),
    'math.fraction.compare':     () => import('../generators/math/frac-compare').then(m => ({ generate: m.generateFracCompare })),
    'math.decimal.addsub':       () => import('../generators/math/dec-add').then(m => ({ generate: m.generateDecAdd })),
    'math.calc.addsub-vertical': () => import('../generators/math/add-carry').then(m => ({ generate: m.generateAddCarry })),
  };

  const loader = genMap[kpId];
  if (!loader) return null;
  const mod = await loader();
  return mod.generate;
}

/**
 * For a given KP, load its question pool.
 * Prefers JSON bank; falls back to generator-produced questions.
 */
async function loadKPQuestions(
  kpId: string,
  chapterNumber: number,
  genSeedBase: number,
  rand: () => number,
): Promise<BankQuestion[]> {
  // Try JSON bank first
  const bankUrl = BANK_FILES[kpId];
  if (bankUrl) {
    try {
      return await loadBankQuestions(bankUrl, chapterNumber);
    } catch {
      // Fall through to generator
    }
  }

  // Try math generator
  const gen = await loadMathGen(kpId);
  if (gen) {
    const questions: BankQuestion[] = [];
    for (let i = 0; i < 30; i++) {
      const d = pickDifficulty(rand);
      const seed = (genSeedBase + i * 31 + chapterNumber * 997) | 0;
      const q = gen(seed, d);
      questions.push({
        ...q,
        surfaceVariant: `gen-${kpId}-${seed}-${d}`,
        chapterNumber,
      });
    }
    return questions;
  }

  return [];
}

// ─── Chapter-quiz difficulty weights ─────────────────────────────────────────
// 章節練習卷: 40% d1, 40% d2, 20% d3
const CHAPTER_QUIZ_DIFFICULTY_WEIGHTS: Difficulty[] = [
  1, 1, 1, 1, 2, 2, 2, 2, 3, 3,
];

function pickChapterQuizDifficulty(rand: () => number): Difficulty {
  return CHAPTER_QUIZ_DIFFICULTY_WEIGHTS[
    Math.floor(rand() * CHAPTER_QUIZ_DIFFICULTY_WEIGHTS.length)
  ];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * composeMockPaper
 *
 * Returns a Question[] of length 30 (midterm) or 50 (final).
 * Only questions from KPs with status === 'ready' are included.
 */
export async function composeMockPaper(opts: MockPaperOpts): Promise<GeneratedQuestion[]> {
  const { subject, level, rngSeed } = opts;
  const seed = rngSeed ?? (Date.now() & 0x7fffffff);
  const rand = mulberry32(seed);

  const curriculum = CURRICULUM.find(s => s.subject === subject);
  if (!curriculum) throw new Error(`Unknown subject: ${subject}`);

  const totalQ = level === 'midterm' ? 30 : 50;
  const chapters = curriculum.chapters;

  // Split chapters into front/back halves
  const midpoint = Math.ceil(chapters.length / 2);
  const frontChapters = chapters.slice(0, midpoint);
  const backChapters = chapters.slice(midpoint);

  // Chapter weights:
  //  midterm: front chapters = 60% of questions, back chapters = 40%
  //  final:   all chapters even weight
  let chapterWeights: Array<{ chapter: typeof chapters[0]; weight: number }>;

  if (level === 'midterm') {
    chapterWeights = [
      ...frontChapters.map(c => ({ chapter: c, weight: 0.6 / Math.max(frontChapters.length, 1) })),
      ...backChapters.map(c => ({ chapter: c, weight: 0.4 / Math.max(backChapters.length, 1) })),
    ];
  } else {
    chapterWeights = chapters.map(c => ({ chapter: c, weight: 1 / chapters.length }));
  }

  // Allocate Q count per chapter (integer, sum = totalQ)
  const rawCounts = chapterWeights.map(cw => ({ ...cw, raw: cw.weight * totalQ }));
  let allocated = rawCounts.map(cw => ({ ...cw, count: Math.floor(cw.raw) }));
  let remainder = totalQ - allocated.reduce((s, cw) => s + cw.count, 0);
  // Distribute remainder to chapters with highest fractional parts
  const fracs = allocated
    .map((cw, i) => ({ i, frac: rawCounts[i].raw - cw.count }))
    .sort((a, b) => b.frac - a.frac);
  // Cycle through fracs if remainder > chapters (handles degenerate edge cases)
  for (let k = 0; k < remainder; k++) allocated[fracs[k % fracs.length].i].count++;

  // Load questions per chapter, filtered to ready KPs
  const result: GeneratedQuestion[] = [];
  const genSeedBase = seed ^ 0xdeadbeef;

  for (const { chapter, count } of allocated) {
    if (count === 0) continue;
    const readyKPs = chapter.kps.filter(k => k.status === 'ready');
    if (readyKPs.length === 0) continue;

    // Collect all available questions from ready KPs in this chapter
    let chapterPool: BankQuestion[] = [];
    for (const kp of readyKPs) {
      const qs = await loadKPQuestions(kp.id, chapter.number, genSeedBase, rand);
      chapterPool.push(...qs);
    }

    if (chapterPool.length === 0) continue;

    // Seeded shuffle and pick `count` questions from this chapter
    // Apply difficulty mix: try to pick 30% d1, 50% d2, 20% d3
    // But since bank questions don't have explicit difficulty, we just shuffle and take
    const shuffled = seededShuffle(chapterPool, rand);
    const picked = shuffled.slice(0, count) as GeneratedQuestion[];
    result.push(...picked);
  }

  // Final seeded shuffle of the full paper
  return seededShuffle(result, rand);
}

// ─── Chapter Quiz ─────────────────────────────────────────────────────────────

export interface ChapterQuizOpts {
  subject: Subject;
  chapterNumber: number;
  /** Number of questions: 10 or 15. Default 10. */
  count?: 10 | 15;
  rngSeed?: number;
}

/**
 * composeChapterQuiz
 *
 * Assembles a 10-15 question chapter practice paper from a single chapter.
 * Difficulty mix: 40% d1, 40% d2, 20% d3.
 * Only KPs with status === 'ready' are included.
 */
export async function composeChapterQuiz(opts: ChapterQuizOpts): Promise<GeneratedQuestion[]> {
  const { subject, chapterNumber, count = 10, rngSeed } = opts;
  const seed = rngSeed ?? (Date.now() & 0x7fffffff);
  const rand = mulberry32(seed);

  const curriculum = CURRICULUM.find(s => s.subject === subject);
  if (!curriculum) throw new Error(`Unknown subject: ${subject}`);

  const chapter = curriculum.chapters.find(c => c.number === chapterNumber);
  if (!chapter) throw new Error(`Chapter ${chapterNumber} not found for ${subject}`);

  const readyKPs = chapter.kps.filter(k => k.status === 'ready');
  if (readyKPs.length === 0) return [];

  const genSeedBase = seed ^ 0xabcdef12;
  let pool: BankQuestion[] = [];

  for (const kp of readyKPs) {
    const qs = await loadKPQuestions(kp.id, chapterNumber, genSeedBase, rand);
    // Attach difficulty weight for chapter-quiz mix. v1.1 worked-example
    // 永遠 d1,不重寫 difficulty;且不該流到 mock-paper(spec §2-3),純 narrow
    // 過濾保險。
    const tagged = qs
      .filter(q => q.kind !== 'worked-example')
      .map(q => ({
        ...q,
        difficulty: (q as unknown as { difficulty?: import('../generators/math/types').Difficulty }).difficulty
          ?? pickChapterQuizDifficulty(rand),
      }));
    pool.push(...tagged as BankQuestion[]);
  }

  if (pool.length === 0) return [];

  const shuffled = seededShuffle(pool, rand);
  return shuffled.slice(0, count) as GeneratedQuestion[];
}

// ─── Sync chapter-quiz variant (for tests) ────────────────────────────────────

export interface SyncChapterQuizOpts {
  count?: 10 | 15;
  questions: GeneratedQuestion[];
  rngSeed?: number;
}

/**
 * Synchronous chapter-quiz composer for unit tests.
 * Accepts a pre-loaded question pool, shuffles, and returns count questions.
 */
export function composeChapterQuizSync(opts: SyncChapterQuizOpts): GeneratedQuestion[] {
  const { count = 10, questions, rngSeed } = opts;
  const rand = mulberry32(rngSeed ?? 42);
  const shuffled = seededShuffle(questions, rand);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

// ─── Sync variant (for tests, uses pre-loaded question arrays) ────────────────

export interface SyncMockPaperOpts {
  level: 'midterm' | 'final';
  /** chapter → questions mapping; all assumed ready */
  chapterPools: Array<{ chapterNumber: number; questions: GeneratedQuestion[] }>;
  rngSeed?: number;
}

/**
 * Synchronous version for unit tests: accepts pre-loaded question pools.
 */
export function composeMockPaperSync(opts: SyncMockPaperOpts): GeneratedQuestion[] {
  const { level, chapterPools, rngSeed } = opts;
  const seed = rngSeed ?? 42;
  const rand = mulberry32(seed);

  const totalQ = level === 'midterm' ? 30 : 50;
  const n = chapterPools.length;
  if (n === 0) return [];

  const midpoint = Math.ceil(n / 2);
  const result: GeneratedQuestion[] = [];

  // Weight per chapter
  const weights: number[] = chapterPools.map((_, i) => {
    if (level === 'final') return 1 / n;
    return i < midpoint
      ? 0.6 / midpoint
      : 0.4 / Math.max(n - midpoint, 1);
  });

  // Allocate integer counts
  const rawCounts = weights.map(w => w * totalQ);
  const counts = rawCounts.map(r => Math.floor(r));
  let rem = totalQ - counts.reduce((s, c) => s + c, 0);
  const fracs = rawCounts
    .map((r, i) => ({ i, frac: r - counts[i] }))
    .sort((a, b) => b.frac - a.frac);
  // Distribute remainder; cycle if rem > number of chapters (e.g. single chapter)
  for (let k = 0; k < rem; k++) counts[fracs[k % fracs.length].i]++;

  for (let i = 0; i < chapterPools.length; i++) {
    const count = counts[i];
    if (count === 0) continue;
    const pool = chapterPools[i].questions;
    if (pool.length === 0) continue;
    const shuffled = seededShuffle(pool, rand);
    result.push(...shuffled.slice(0, count));
  }

  return seededShuffle(result, rand);
}
